import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { ShieldCheck, Clock3, Package, CalendarClock } from "lucide-react";
import authHeader from "../../../Auth/authHeader";
import { ApiLink } from "../../../APILink/ApiLink";
import "./SoftwareLicenseTab.scss";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("sq-AL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const statusClass = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("active") || text.includes("aktiv")) return "is-active";
  if (text.includes("expire") || text.includes("skad")) return "is-expired";
  if (text.includes("paid") || text.includes("paguar")) return "is-paid";
  if (text.includes("pending") || text.includes("pritje")) return "is-pending";
  return "is-neutral";
};

export default function SoftwareLicenseTab({ companyId, accountIdentificationNumber }) {
  const [loading, setLoading] = useState(true);
  const [activeLicense, setActiveLicense] = useState(null);
  const [modules, setModules] = useState([]);
  const [history, setHistory] = useState([]);
  const [billingLines, setBillingLines] = useState([]);

  useEffect(() => {
    loadLicenseData();
  }, [companyId]);

  useEffect(() => {
    loadBillingLines();
  }, [companyId, accountIdentificationNumber]);

  const loadLicenseData = async () => {
    try {
      setLoading(true);
      const headers = authHeader();
      const response = await axios.get(ApiLink.API_URL + "LicenseStatus/details", {
        headers,
        params: companyId ? { companyId } : {},
      });

      setActiveLicense(response?.data?.activeLicense || null);
      setModules(Array.isArray(response?.data?.modules) ? response.data.modules : []);
      setHistory(Array.isArray(response?.data?.history) ? response.data.history : []);
    } catch (error) {
      console.error("Failed to load license details", error);
      toast.error("Nuk u ngarkuan te dhenat e licenses.");
    } finally {
      setLoading(false);
    }
  };

  const loadBillingLines = async () => {
    try {
      if (!accountIdentificationNumber) {
        setBillingLines([]);
        return;
      }

      const headers = authHeader();
      const response = await axios.get(ApiLink.API_URL + "LicenseStatus/billing-lines", {
        headers,
        params: {
          accountIdentificationNumber,
        },
      });

      setBillingLines(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load billing lines", error);
      toast.error("Nuk u ngarkuan linjat e faturimit.");
    }
  };

  const formatAmount = (value) => {
    if (value == null || Number.isNaN(Number(value))) return "-";
    return new Intl.NumberFormat("sq-AL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const paymentLabel = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return "-";
    return numeric === 0 ? "Pa paguar" : "E paguar";
  };

  const formatInvoiceNumber = (row) => {
    const fiscalYear = row?.fiscalYear;
    const invoiceNR = row?.invoiceNR;
    if (fiscalYear == null || !invoiceNR) return "-";
    return `IND-${fiscalYear}-${invoiceNR}`;
  };

  const activeModules = useMemo(
    () => modules.filter((module) => module?.isActive),
    [modules]
  );

  if (loading) {
    return (
      <div className="software-license-tab software-license-tab--loading">
        Duke ngarkuar te dhenat e licenses...
      </div>
    );
  }

  return (
    <div className="software-license-tab">
      <section className="software-license-tab__section">
        <header className="software-license-tab__section-head">
          <h3>Licensa Aktive</h3>
        </header>

        {activeLicense ? (
          <div className="software-license-tab__active-grid">
            <article className="software-license-tab__metric-card">
              <div className="software-license-tab__metric-icon-wrap">
                <Package className="software-license-tab__metric-icon" />
              </div>
              <div>
                <p className="software-license-tab__metric-label">Paketa</p>
                <p className="software-license-tab__metric-value">
                  {activeLicense.packageName || "-"}
                </p>
              </div>
            </article>

            <article className="software-license-tab__metric-card">
              <div className="software-license-tab__metric-icon-wrap">
                <ShieldCheck className="software-license-tab__metric-icon" />
              </div>
              <div>
                <p className="software-license-tab__metric-label">Statusi</p>
                <p className="software-license-tab__metric-value">
                  <span className={`software-license-tab__chip ${statusClass(activeLicense.statusName)}`}>
                    {activeLicense.statusName || "-"}
                  </span>
                </p>
              </div>
            </article>

            <article className="software-license-tab__metric-card">
              <div className="software-license-tab__metric-icon-wrap">
                <Clock3 className="software-license-tab__metric-icon" />
              </div>
              <div>
                <p className="software-license-tab__metric-label">Pagesa</p>
                <p className="software-license-tab__metric-value">
                  <span
                    className={`software-license-tab__chip ${statusClass(
                      activeLicense.paymentStatusName
                    )}`}
                  >
                    {activeLicense.paymentStatusName || "-"}
                  </span>
                </p>
              </div>
            </article>

            <article className="software-license-tab__metric-card">
              <div className="software-license-tab__metric-icon-wrap">
                <CalendarClock className="software-license-tab__metric-icon" />
              </div>
              <div>
                <p className="software-license-tab__metric-label">Perfundon me</p>
                <p className="software-license-tab__metric-value">
                  {formatDate(activeLicense.endDate)}
                </p>
                <p className="software-license-tab__metric-subtext">
                  {typeof activeLicense.daysLeft === "number"
                    ? `${activeLicense.daysLeft} dite te mbetura`
                    : "-"}
                </p>
              </div>
            </article>
          </div>
        ) : (
          <div className="software-license-tab__empty-state">
            Nuk u gjet asnje license aktive per kete kompani.
          </div>
        )}

        <div className="software-license-tab__details-grid">
          <div className="software-license-tab__detail-card">
            <p className="software-license-tab__detail-label">Lloji i licenses</p>
            <p className="software-license-tab__detail-value">
              {activeLicense?.licenseTypeName || "-"}
            </p>
          </div>
          <div className="software-license-tab__detail-card">
            <p className="software-license-tab__detail-label">Data e fillimit</p>
            <p className="software-license-tab__detail-value">
              {formatDate(activeLicense?.startDate)}
            </p>
          </div>
          <div className="software-license-tab__detail-card">
            <p className="software-license-tab__detail-label">Data e krijimit</p>
            <p className="software-license-tab__detail-value">
              {formatDate(activeLicense?.createdDate)}
            </p>
          </div>
          <div className="software-license-tab__detail-card">
            <p className="software-license-tab__detail-label">Krijuar nga</p>
            <p className="software-license-tab__detail-value">
              {activeLicense?.createdByUser || "-"}
            </p>
          </div>
        </div>

        <div className="software-license-tab__modules">
          <h4>Modulet aktive</h4>
          {activeModules.length ? (
            <div className="software-license-tab__module-list">
              {activeModules.map((module) => (
                <span key={module.moduleId} className="software-license-tab__module-pill">
                  {module.moduleName}
                </span>
              ))}
            </div>
          ) : (
            <p className="software-license-tab__modules-empty">Nuk ka module aktive.</p>
          )}
        </div>
      </section>

      <section className="software-license-tab__section">
        <header className="software-license-tab__section-head">
          <h3>Linjat e Faturimit</h3>
          <span className="software-license-tab__history-count">{billingLines.length} fatura</span>
        </header>

        <div className="software-license-tab__billing-table-wrap">
          <table className="software-license-tab__billing-table">
            <thead>
              <tr>
                <th>Nr. i Fatures</th>
                <th>Totali</th>
                <th>Data</th>
                <th>Pagesa</th>
                <th>Viti Fiskal</th>
              </tr>
            </thead>
            <tbody>
              {billingLines.length ? (
                billingLines.map((row, idx) => {
                  const paid = Number(row.paymentOptions) !== 0;
                  return (
                    <tr key={`${row.invoiceNR || "inv"}-${idx}`}>
                      <td>{formatInvoiceNumber(row)}</td>
                      <td>{formatAmount(row.total)}</td>
                      <td>{formatDate(row.date)}</td>
                      <td>
                        <span
                          className={`software-license-tab__chip ${paid ? "is-paid" : "is-expired"}`}
                        >
                          {paymentLabel(row.paymentOptions)}
                        </span>
                      </td>
                      <td>{row.fiscalYear ?? "-"}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="software-license-tab__history-empty">
                    Nuk ka linja faturimi per kete numer identifikues.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="software-license-tab__section">
        <header className="software-license-tab__section-head">
          <h3>Historiku i Licensave</h3>
          <span className="software-license-tab__history-count">{history.length} regjistrime</span>
        </header>

        <div className="software-license-tab__history-table-wrap">
          <table className="software-license-tab__history-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Paketa</th>
                <th>Lloji</th>
                <th>Periudha</th>
                <th>Statusi</th>
                <th>Pagesa</th>
                <th>Koment</th>
              </tr>
            </thead>
            <tbody>
              {history.length ? (
                history.map((row, idx) => (
                  <tr key={row.licenseId || idx}>
                    <td>{row.licenseId || "-"}</td>
                    <td>{row.packageName || "-"}</td>
                    <td>{row.licenseTypeName || "-"}</td>
                    <td>
                      {formatDate(row.startDate)} - {formatDate(row.endDate)}
                    </td>
                    <td>
                      <span className={`software-license-tab__chip ${statusClass(row.statusName)}`}>
                        {row.statusName || "-"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`software-license-tab__chip ${statusClass(
                          row.paymentStatusName
                        )}`}
                      >
                        {row.paymentStatusName || "-"}
                      </span>
                    </td>
                    <td className="software-license-tab__comment-cell">{row.comment || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="software-license-tab__history-empty">
                    Nuk ka historik licensash.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
